import { PropertyTypes } from "epsm-api-types";
import type { MatrixQuarterlyReport } from "epsm-api-types/models";
import {
	CandlestickSeries,
	ColorType,
	createChart,
	createSeriesMarkers,
	HistogramSeries,
	type IChartApi,
	type ISeriesApi,
	type LogicalRange,
	type SeriesMarker,
	type UTCTimestamp,
} from "lightweight-charts";
import { useCallback, useEffect, useRef, useState } from "react";
import { NoData } from "@/components/NoData.js";
import { settings } from "@/lib/utils/settings/settings.js";
import { ONE_DAY_IN_MS, shiftDateToLocalTimeZone } from "@/lib/utils.js";
import ErrorHandler from "@/services/ErrorService/ErrorService.js";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common.js";
import { formatValue } from "../../../helpers/FormattingHelpers.js";
import { chartColors } from "../../const.js";
import { fetchDataForChart } from "../../helpers/fetchDataForChart/fetchDataForChart.js";
import { updateVolumeData } from "../../helpers/updateVolumeData";
import { visibleLogicalRangeHandler } from "../../helpers/visibleLogicalRangeHandler";
import {
	type PriceInfo,
	TIMEFRAME_MAP,
} from "../../services/PriceService/PriceService.js";
import { UseData } from "../../services/UseData.js";
import { UseQuarterlyReportData } from "../../services/UseQuarterlyReportData.js";
import type { ChartProps } from "../Chart.js";

const {
	background: backgroundColor,
	dot: dotColor,
	text: textColor,
	border: borderColor,
	up: upColor,
	down: downColor,
	volumesUp: volumesUpColor,
	volumesDown: volumesDownColor,
} = chartColors;

export const LightweightChart: React.FC<ChartProps> = ({ state }) => {
	const ticker = state.ticker ?? DEFAULT_STATE_VALUES.ticker;
	const timeFrame = state.timeFrame ?? DEFAULT_STATE_VALUES.timeFrame;
	const isPartOfWidget = state?.isPartOfWidget ?? false;
	const showFullRange = state?.showFullRange ?? false;

	const currentTime = useRef(new Date(Date.now()).getTime());
	const duration = useRef(0);

	const { isLoading, error, execute } = UseData.create().useGetData();

	const { execute: executeQuarterly } =
		UseQuarterlyReportData.create().useGetQuarterlyReports();

	const [from, setFrom] = useState<number>(currentTime.current);
	const [chart, setChart] = useState<IChartApi | null>(null);
	const [hasData, setHasData] = useState(true);
	const [quarterlyReports, setQuarterlyReports] = useState<
		MatrixQuarterlyReport[]
	>([]);
	const isFullRangeSetRef = useRef(false);

	const shouldProcessReloadRef = useRef<boolean>(true);

	const onVisibleLogicalRangeChangeHandler = useCallback(
		(newVisibleLogicalRange: LogicalRange | null) =>
			visibleLogicalRangeHandler({
				newVisibleLogicalRange,
				from,
				duration: duration.current,
				isLoading,
				hasData,
				setFrom,
			}),
		[from, isLoading, hasData],
	);

	const pricesRef = useRef<PriceInfo[]>([]);

	// we need to keep the reference to the series to pass an actual series to the ws handler while changing a timeframe
	const mainSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
	const mainSeries = mainSeriesRef.current;

	const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
	const volumeSeries = volumeSeriesRef.current;

	useEffect(() => {
		if (mainSeries && volumeSeries) {
			mainSeriesRef.current = mainSeries;
			volumeSeriesRef.current = volumeSeries;
		}
	}, [mainSeries, volumeSeries]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't need other dependencies to prevent infinite loop
	useEffect(() => {
		if (!ticker || !mainSeries || !volumeSeries || !chart) {
			return;
		}

		const timeFrameInfo = TIMEFRAME_MAP[timeFrame];
		const eventSource = new EventSource(
			`${settings.wsHost}/live/${TIMEFRAME_MAP[timeFrame].wsType}/${ticker}`,
		);
		shouldProcessReloadRef.current = true;

		const processData = (newPrice: PriceInfo) => {
			const fraction = timeFrameInfo.duration / 1000;
			const newTimeDivision = Math.trunc(
				shiftDateToLocalTimeZone(newPrice.time) / fraction / 1000,
			);

			const prices = mainSeries.data() || [];
			const p = prices.find(
				(price) =>
					Math.trunc((price.time as number) / fraction) === newTimeDivision,
			);

			const isHistorical =
				newTimeDivision <
				Math.trunc(((prices.at(-1)?.time as number) ?? 0) / fraction);

			if (isHistorical && !p) {
				// we cannot update non existing points on the chart
				return;
			}

			const newItem = {
				...newPrice,
				time: (newTimeDivision * fraction) as UTCTimestamp,
			};

			if (p) {
				newItem.time = p.time as UTCTimestamp;
				newItem.low = Math.min(
					newItem.low,
					(p.customValues?.low as number) ?? newItem.low,
				);
				newItem.high = Math.max(
					newItem.high,
					(p.customValues?.high as number) ?? newItem.high,
				);
				newItem.volume = Math.max(
					newItem.volume ?? 0,
					(p.customValues?.volume as number) ?? newItem.volume,
				);
			}

			const updatedVolumeData = updateVolumeData(
				[newItem],
				volumesUpColor,
				volumesDownColor,
			);

			mainSeries.update(newItem, isHistorical);
			volumeSeries.update(updatedVolumeData[0], isHistorical);
		};

		eventSource.onerror = (error) => {
			ErrorHandler.logError(error);
		};

		eventSource.onmessage = (event) => {
			const prices = JSON.parse(event.data) as PriceInfo[];

			for (const price of prices) {
				processData(price);
			}
		};

		eventSource.addEventListener("reload", () => {
			if (shouldProcessReloadRef.current) {
				shouldProcessReloadRef.current = false;
				fetchDataForChart({
					ticker,
					volumeSeries,
					mainSeries,
					chart,
					duration,
					from,
					timeFrame,
					quarterlyReportsRef,
					pricesRef,
					showFullRange,
					isFullRangeSetRef,
					setQuarterlyReports,
					setHasData,
					execute,
					executeQuarterly,
				});
			}
		});

		return () => {
			eventSource?.close();
		};
	}, [ticker, timeFrame, mainSeries, volumeSeries, chart]);

	const quarterlyReportsRef = useRef<MatrixQuarterlyReport[]>([]);
	const chartContainerRef = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		let active = true;
		const CANDLES_TO_FETCH = 400;
		duration.current =
			CANDLES_TO_FETCH * TIMEFRAME_MAP[timeFrame].duration + 7 * ONE_DAY_IN_MS;
		pricesRef.current = [];
		quarterlyReportsRef.current = [];
		currentTime.current = new Date(Date.now()).getTime();

		if (active) {
			setFrom(currentTime.current - duration.current);
		}

		return () => {
			active = false;
		};
	}, [ticker, timeFrame]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		fetchDataForChart({
			ticker,
			volumeSeries,
			mainSeries,
			chart,
			duration,
			from,
			timeFrame,
			quarterlyReportsRef,
			pricesRef,
			showFullRange,
			isFullRangeSetRef,
			setQuarterlyReports,
			setHasData,
			execute,
			executeQuarterly,
		});
	}, [execute, executeQuarterly, from, showFullRange, timeFrame]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <we don't need backgroundColor in dependencies>
	useEffect(() => {
		if (!chartContainerRef.current) {
			return;
		}

		const chart = createChart(chartContainerRef.current, {
			layout: {
				background: { type: ColorType.Solid, color: backgroundColor },
				textColor,
				fontSize: isPartOfWidget ? 10 : 14,
			},
			grid: {
				vertLines: { color: "black" },
				horzLines: { color: "black" },
			},
			timeScale: {
				visible: true,
				timeVisible: TIMEFRAME_MAP[timeFrame].isIntraday,
				secondsVisible: false,
				minimumHeight: 5,
			},

			rightPriceScale: {
				scaleMargins: {
					top: 0.1,
					bottom: 0.1,
				},
				minimumWidth: 5,
			},
			autoSize: true,
		});

		setChart(chart);

		chart.priceScale("right").applyOptions({
			borderColor: isPartOfWidget ? "var(--background)" : borderColor,
		});

		chart.timeScale().fitContent();
		chart.timeScale().applyOptions({
			minimumHeight: 5,
		});

		const mainSeries = chart.addSeries(CandlestickSeries, {
			upColor,
			downColor,
			borderUpColor: upColor,
			borderDownColor: downColor,
			wickUpColor: upColor,
			wickDownColor: downColor,
			priceLineVisible: false,
		});

		mainSeriesRef.current = mainSeries;

		mainSeries.priceScale().applyOptions({
			scaleMargins: {
				top: 0.1,
				bottom: 0.3,
			},
		});

		const volumeSeries = chart.addSeries(HistogramSeries, {
			priceFormat: {
				type: "volume",
			},
			priceScaleId: "",
			priceLineVisible: false,
		});

		volumeSeriesRef.current = volumeSeries;

		volumeSeries.priceScale().applyOptions({
			scaleMargins: {
				top: 0.8,
				bottom: 0,
			},
		});

		return () => {
			chart
				.timeScale()
				.unsubscribeVisibleLogicalRangeChange(
					onVisibleLogicalRangeChangeHandler,
				);
			volumeSeries?.applyOptions({ visible: false });
			mainSeries?.applyOptions({ visible: false });
			chart.remove();
			setChart(null);
			mainSeriesRef.current = null;
			volumeSeriesRef.current = null;
		};
	}, [timeFrame, ticker]);

	useEffect(() => {
		if (!chart || !mainSeries) {
			return;
		}

		let seriesMarkers = createSeriesMarkers(mainSeries, []);

		if (quarterlyReports.length) {
			const markers: SeriesMarker<UTCTimestamp>[] = quarterlyReports.map(
				(item) => {
					const time = item.earning_date
						? (Math.trunc(
								shiftDateToLocalTimeZone(item.earning_date) / 1000,
							) as UTCTimestamp)
						: undefined;

					return {
						time: time as UTCTimestamp,
						position: "aboveBar",
						color: dotColor,
						shape: "circle",
						text: `EM ${formatValue(item.em?.value, PropertyTypes.NUMBER, { maximumFractionDigits: 0 })}${item.em?.partial ? "*" : ""}`,
						size: 1.4,
					};
				},
			);

			seriesMarkers = createSeriesMarkers(mainSeries, markers);
		}
		return () => seriesMarkers.setMarkers([]);
	}, [mainSeries, quarterlyReports, chart]);

	useEffect(() => {
		if (!chart) {
			return;
		}

		chart
			?.timeScale()
			.subscribeVisibleLogicalRangeChange(onVisibleLogicalRangeChangeHandler);

		return () => {
			chart
				?.timeScale()
				.unsubscribeVisibleLogicalRangeChange(
					onVisibleLogicalRangeChangeHandler,
				);
		};
	}, [chart, onVisibleLogicalRangeChangeHandler]);

	return (
		<div className="h-full relative">
			<div className="h-full" ref={chartContainerRef} />

			{error ? (
				<div className="absolute text-gold top-1/2 right-1/2 transform translate-x-1/2 -translate-y-1/2 z-10">
					<NoData />
				</div>
			) : null}
		</div>
	);
};
